/**
 * Portfolio 选图配置：bridge preset、accept、cancel 轮询间隔、session 取消检测开关。
 *
 * - iOS PDF：较长 cancelPollMs（文档选择器延迟写 files）
 * - Android：较长 cancelPollMs + visibility/blur（见 resolvePortfolioFilePickCancelDetection）
 * - session 的 focus/blur/visibility 须在 click 之后 activate（见 runPortfolioFilePickSession）
 *
 * @see apps/token-seek/features/20260610-portfolio-file-pick-platform-strategy/SPEC.md
 */
import type { PrepareFileInputParams } from "@/src/lib/bridge/prepareFileInput";
import type { PortfolioUploadType } from "@/src/components/PortfolioScreenshotModal/types";
import { PORTFOLIO_PDF_ACCEPT } from "../../constants/uploadConstants";

/** 桌面 Web / iOS 图片：focus 回来后默认轮询间隔（ms） */
export const PICK_CANCEL_POLL_MS = 400;

/**
 * iOS document picker often populates `input.files` after focus returns,
 * sometimes without firing `change` in time for a short poll window.
 */
export const PICK_CANCEL_POLL_MS_IOS_DOCUMENT = 1500;

/**
 * Android in-app gallery / document pickers may deliver files after focus + delay.
 */
export const PICK_CANCEL_POLL_MS_ANDROID_IN_APP = 1200;

const PORTFOLIO_IMAGE_PICK_PRESET: PrepareFileInputParams = {
  mode: "library",
  accept: "image/*",
  capture: null,
  multiple: true,
};

export type PortfolioPickConfig = {
  preset: PrepareFileInputParams;
  accept: string;
  multiple: boolean;
  cancelPollMs: number;
};

export type ResolvePortfolioPickConfigOptions = {
  isIosInApp?: boolean;
  isAndroidInApp?: boolean;
};

/** Which events to use for picker cancel / delayed file delivery. */
export type PortfolioFilePickCancelDetection = {
  useWindowFocus: boolean;
  useVisibilityChange: boolean;
  useInputBlur: boolean;
};

/**
 * iOS: `change` + deferred `focus` (blur/visibility off — they false-cancel before picker opens).
 * Android: full deferred detection for WebView gallery quirks.
 * Web: `change` + deferred `focus`.
 *
 * Call {@link runPortfolioFilePickSession}'s `activateCancelDetection` only after `click()`.
 */
export function resolvePortfolioFilePickCancelDetection(
  options?: ResolvePortfolioPickConfigOptions,
): PortfolioFilePickCancelDetection {
  if (options?.isAndroidInApp) {
    return {
      useWindowFocus: true,
      useVisibilityChange: true,
      useInputBlur: true,
    };
  }

  return {
    useWindowFocus: true,
    useVisibilityChange: false,
    useInputBlur: false,
  };
}

export function resolvePortfolioPickConfig(
  uploadType: PortfolioUploadType,
  options?: ResolvePortfolioPickConfigOptions,
): PortfolioPickConfig {
  const isIosInApp = options?.isIosInApp ?? false;
  const isAndroidInApp = options?.isAndroidInApp ?? false;

  if (uploadType === "pdf") {
    const preset: PrepareFileInputParams = {
      mode: "document",
      accept: PORTFOLIO_PDF_ACCEPT,
      capture: null,
      multiple: true,
    };
    let cancelPollMs = PICK_CANCEL_POLL_MS;
    if (isIosInApp) {
      cancelPollMs = PICK_CANCEL_POLL_MS_IOS_DOCUMENT;
    } else if (isAndroidInApp) {
      cancelPollMs = PICK_CANCEL_POLL_MS_ANDROID_IN_APP;
    }
    return {
      preset,
      accept: PORTFOLIO_PDF_ACCEPT,
      multiple: true,
      cancelPollMs,
    };
  }

  return {
    preset: PORTFOLIO_IMAGE_PICK_PRESET,
    accept: PORTFOLIO_IMAGE_PICK_PRESET.accept,
    multiple: PORTFOLIO_IMAGE_PICK_PRESET.multiple,
    cancelPollMs: isAndroidInApp
      ? PICK_CANCEL_POLL_MS_ANDROID_IN_APP
      : PICK_CANCEL_POLL_MS,
  };
}
